import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase, formatAuthError } from '@/lib/supabase';
import { Mail, KeyRound } from 'lucide-react';

type LoginMethod = 'magic-link' | 'otp';

export function MagicLinkLogin() {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [method, setMethod] = useState<LoginMethod>('otp');
  const [otpSent, setOtpSent] = useState(false);
  const navigate = useNavigate();

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (method === 'magic-link') {
        // Send magic link
        await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/home`,
          },
        });
        setSuccess(true);
      } else {
        // Send OTP code
        await supabase.auth.signInWithOtp({
          email,
          options: {
            shouldCreateUser: false, // Don't create new users via OTP
          },
        });
        setOtpSent(true);
      }
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email',
      });

      if (error) throw error;

      // Navigate to dashboard on success
      navigate('/home');
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Success state for magic link
  if (success) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-ingesta-blue-600">
            Check Your Email
          </CardTitle>
          <CardDescription>
            Magic link sent successfully
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center text-sm text-muted-foreground">
              We've sent a magic link to <strong>{email}</strong>.
              Click the link in your email to sign in automatically.
            </div>
            <div className="text-center text-sm text-muted-foreground">
              The link will expire in 1 hour.
            </div>
            <div className="text-center text-sm text-muted-foreground pt-2">
              Make sure to check your spam folder if you don't see the email.
            </div>
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
              variant="outline"
            >
              Back to Login
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // OTP verification state
  if (otpSent && method === 'otp') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-ingesta-blue-600">
            Enter Verification Code
          </CardTitle>
          <CardDescription>
            We sent a 6-digit code to {email}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="otp" className="text-sm font-medium">
                Verification Code
              </label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-lg tracking-widest"
                required
              />
            </div>
            {error && (
              <div className="text-red-600 text-sm text-center">{error}</div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || otp.length !== 6}
            >
              {isLoading ? 'Verifying...' : 'Verify Code'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setOtpSent(false);
                setOtp('');
                setError('');
              }}
            >
              Use a different email
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  // Initial state - choose method and enter email
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold text-ingesta-blue-600">
          Passwordless Sign In
        </CardTitle>
        <CardDescription>
          Choose your preferred login method
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Method selection */}
        <div className="mb-6 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setMethod('otp')}
            className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
              method === 'otp'
                ? 'border-ingesta-blue-600 bg-ingesta-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <KeyRound className={`w-6 h-6 ${method === 'otp' ? 'text-ingesta-blue-600' : 'text-gray-600'}`} />
            <span className={`text-sm font-medium ${method === 'otp' ? 'text-ingesta-blue-600' : 'text-gray-700'}`}>
              Email Code
            </span>
          </button>
          <button
            type="button"
            onClick={() => setMethod('magic-link')}
            className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
              method === 'magic-link'
                ? 'border-ingesta-blue-600 bg-ingesta-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Mail className={`w-6 h-6 ${method === 'magic-link' ? 'text-ingesta-blue-600' : 'text-gray-600'}`} />
            <span className={`text-sm font-medium ${method === 'magic-link' ? 'text-ingesta-blue-600' : 'text-gray-700'}`}>
              Magic Link
            </span>
          </button>
        </div>

        <form onSubmit={handleSendOtp} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : method === 'otp' ? 'Send Code' : 'Send Magic Link'}
          </Button>
        </form>
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Prefer to use a password?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-ingesta-blue-600 hover:underline"
            >
              Sign in with password
            </button>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
